import { useState, useEffect } from 'react';
import { X, Link2, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useShareStore } from '@/stores/shareStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { SharePermissions, ShareProfile } from '@/types/sharing';
import { PERMISSION_LABELS, DEFAULT_PERMISSIONS } from '@/types/sharing';

interface Props {
  propertyId: string;
  onClose: () => void;
}

export function CreateShareLinkModal({ propertyId, onClose }: Props) {
  const { profiles, fetchProfiles, createLink } = useShareStore();
  const [step, setStep] = useState<'select' | 'created'>('select');
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [useCustom, setUseCustom] = useState(false);
  const [customPermissions, setCustomPermissions] = useState<SharePermissions>(DEFAULT_PERMISSIONS);
  const [linkName, setLinkName] = useState('');
  const [expiresIn, setExpiresIn] = useState<'never' | '7d' | '30d' | '90d'>('never');
  const [isCreating, setIsCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const systemProfiles = profiles.filter((p) => p.user_id === null);
  const userProfiles = profiles.filter((p) => p.user_id !== null);

  const handleCreate = async () => {
    if (!useCustom && !selectedProfileId) {
      toast.error('Bitte wähle ein Profil aus oder passe die Berechtigungen an');
      return;
    }

    setIsCreating(true);
    try {
      let expiresAt: string | null = null;
      if (expiresIn !== 'never') {
        const days = { '7d': 7, '30d': 30, '90d': 90 }[expiresIn];
        const date = new Date();
        date.setDate(date.getDate() + days);
        expiresAt = date.toISOString();
      }

      const link = await createLink({
        property_id: propertyId,
        profile_id: useCustom ? null : selectedProfileId,
        custom_permissions: useCustom ? customPermissions : null,
        name: linkName || null,
        expires_at: expiresAt,
      });

      setCreatedToken(link.token);
      setStep('created');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Erstellen des Links';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const shareUrl = `${window.location.origin}/s/${createdToken}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link kopiert');
    setTimeout(() => setCopied(false), 2000);
  };

  const togglePermission = (key: keyof SharePermissions) => {
    setCustomPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="relative bg-background border border-border rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-foreground-muted" />
            <h2 className="font-semibold">Freigabe-Link erstellen</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-background-subtle"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          <AnimatePresence mode="wait">
            {step === 'select' ? (
              <motion.div
                key="select"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-5"
              >
                {/* Link-Name */}
                <div className="space-y-1.5">
                  <Label>Name des Links (optional)</Label>
                  <Input
                    placeholder="z.B. Für Steuerberater Müller"
                    value={linkName}
                    onChange={(e) => setLinkName(e.target.value)}
                  />
                  <p className="text-xs text-foreground-muted">Intern zur Identifikation des Links</p>
                </div>

                {/* Ablauf */}
                <div className="space-y-1.5">
                  <Label>Link-Ablauf</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {(['never', '7d', '30d', '90d'] as const).map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setExpiresIn(opt)}
                        className={cn(
                          'py-2 text-sm rounded-lg border transition-colors',
                          expiresIn === opt
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-border text-foreground-muted hover:border-border-hover hover:text-foreground'
                        )}
                      >
                        {opt === 'never' ? '∞' : opt === '7d' ? '7 Tage' : opt === '30d' ? '30 Tage' : '90 Tage'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Profil-Auswahl */}
                <div className="space-y-2">
                  <Label>Freigabe-Profil</Label>

                  {/* System-Profile */}
                  {systemProfiles.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs text-foreground-muted font-medium uppercase tracking-wide">Vorlagen</p>
                      <div className="space-y-1">
                        {systemProfiles.map((profile) => (
                          <ProfileOption
                            key={profile.id}
                            profile={profile}
                            isSelected={!useCustom && selectedProfileId === profile.id}
                            onSelect={() => {
                              setSelectedProfileId(profile.id);
                              setUseCustom(false);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Eigene Profile */}
                  {userProfiles.length > 0 && (
                    <div className="space-y-1.5 mt-2">
                      <p className="text-xs text-foreground-muted font-medium uppercase tracking-wide">Meine Profile</p>
                      <div className="space-y-1">
                        {userProfiles.map((profile) => (
                          <ProfileOption
                            key={profile.id}
                            profile={profile}
                            isSelected={!useCustom && selectedProfileId === profile.id}
                            onSelect={() => {
                              setSelectedProfileId(profile.id);
                              setUseCustom(false);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Benutzerdefiniert */}
                  <button
                    onClick={() => {
                      setUseCustom(true);
                      setSelectedProfileId(null);
                    }}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-3 rounded-xl border transition-colors text-left',
                      useCustom
                        ? 'border-foreground bg-background-elevated'
                        : 'border-border hover:border-border-hover'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-base">⚙️</span>
                      <span className="text-sm font-medium">Benutzerdefiniert</span>
                    </div>
                    {useCustom ? (
                      <ChevronUp className="w-4 h-4 text-foreground-muted" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-foreground-muted" />
                    )}
                  </button>

                  {/* Custom Permissions */}
                  {useCustom && (
                    <div className="border border-border rounded-xl p-3 space-y-2">
                      {(Object.keys(PERMISSION_LABELS) as Array<keyof SharePermissions>).map((key) => (
                        <label key={key} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={customPermissions[key]}
                            onChange={() => togglePermission(key)}
                            className="w-4 h-4 rounded accent-foreground"
                          />
                          <span className="text-sm">{PERMISSION_LABELS[key]}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleCreate}
                  isLoading={isCreating}
                  className="w-full"
                  disabled={!useCustom && !selectedProfileId}
                >
                  Link erstellen
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="created"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-5 text-center"
              >
                <div className="w-14 h-14 rounded-full bg-background-subtle flex items-center justify-center mx-auto">
                  <Link2 className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Link erstellt!</h3>
                  <p className="text-sm text-foreground-muted mt-1">
                    Teile diesen Link mit dem Empfänger. Er benötigt keinen Account.
                  </p>
                </div>

                <div className="bg-background-subtle rounded-xl p-3 flex items-center gap-2">
                  <p className="text-sm text-foreground-muted flex-1 truncate">{shareUrl}</p>
                  <button
                    onClick={handleCopy}
                    className="p-2 rounded-lg hover:bg-background-elevated shrink-0"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-accent-green" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleCopy} className="flex-1" variant="secondary">
                    {copied ? 'Kopiert!' : 'Link kopieren'}
                  </Button>
                  <Button onClick={onClose} className="flex-1">
                    Fertig
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

function ProfileOption({
  profile,
  isSelected,
  onSelect,
}: {
  profile: ShareProfile;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const permCount = Object.values(profile.permissions).filter(Boolean).length;
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-3 rounded-xl border transition-colors text-left',
        isSelected
          ? 'border-foreground bg-background-elevated'
          : 'border-border hover:border-border-hover'
      )}
    >
      <span className="text-lg">{profile.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{profile.name}</p>
        <p className="text-xs text-foreground-muted">{permCount} Bereiche freigegeben</p>
      </div>
      {isSelected && (
        <div className="w-4 h-4 rounded-full bg-foreground flex items-center justify-center shrink-0">
          <Check className="w-2.5 h-2.5 text-background" />
        </div>
      )}
    </button>
  );
}
